import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router/composables';

import PosterGraphql from '@/components/poster/PosterGraphql.vue';
import TitleDetailLink from '@/components/nav/links/TitleDetailLink.vue';
import HiddenHorizontalScrollbar from '@/components/HiddenHorizontalScrollbar.vue';

import { ImpressionTrackingEvents } from '@/enums/events';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';

import { useBackendTracking } from '@/helpers/composables/useBackendTracking';

import { SraEventAction } from '@/@types/graphql-types';
import type { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';
import type { PosterGraphqlProps } from '@/interfaces/components/poster/PosterGraphql';
import type { SponsoredAdFragment } from '@/features/sponsored-recommendations/graphql/fragments/SponsoredAd.fragment';
import type {
	HorizontalTitleListItem,
	PosterClickEventPayload,
	PosterClickOutEventPayload,
	HorizontalTitleListItemPayload,
} from './types';
import { useUserStore } from '@/stores/pinia/useUserStore';
import { storeToRefs } from 'pinia';


export default /*#__PURE__*/_defineComponent({
  __name: 'HorizontalTitleList',
  props: {
    clickSource: { default: '' },
    titles: { default: () => [] },
    sponsoredAd: null,
    maxTitles: { default: 0 },
    showRibbon: { type: Boolean },
    showCheck: { type: Boolean },
    showQuickActions: { type: Boolean },
    forceQuickActions: { type: Boolean },
    hasWatchNow: { type: Boolean, default: true },
    showPriceDrop: { type: Boolean },
    showTitle: { type: Boolean },
    showTvBadge: { type: Boolean, default: true },
    isBlocking: { type: Boolean },
    smallPosters: { type: Boolean },
    preventDefault: { type: Boolean },
    resetScrollOnChange: { type: Boolean },
    hasMoreLikeThis: { type: Boolean },
    additionalContexts: { default: () => [] },
    imgsLoadCount: { default: 0 },
    emitOnScroll: { type: Boolean },
    showPosition: { type: Boolean },
    showPositionColor: { default: 'primary' },
    fadedEdge: { type: Boolean },
    isPressPage: { type: Boolean },
    showFreeTag: { type: Boolean, default: false },
    moduleGroup: { default: '' },
    loading: { default: 'eager' }
  },
  emits: ["mounted", "onScrollRight", "scrollbarClicked", "clicked", "hovered", "openModal", "onPosterClickOut", "onPosterClick"],
  setup(__props: any, { emit }: { emit: ({
	(e: 'mounted'): void;
	(e: 'onScrollRight', complete: () => void): void;
	(e: 'scrollbarClicked', label: 'left' | 'right'): void;
	(e: 'clicked', data: HorizontalTitleListItemPayload): void;
	(e: 'hovered', data: HorizontalTitleListItemPayload): void;
	(e: 'openModal', data: HorizontalTitleListItemPayload): void;
	(e: 'onPosterClickOut', data: PosterClickOutEventPayload): void;
	(e: 'onPosterClick', data: PosterClickEventPayload): void;
}), expose: any, slots: any, attrs: any }) {

const props = __props as { clickSource: string, titles: HorizontalTitleListItem[], sponsoredAd?: SponsoredAdFragment, maxTitles: number, showRibbon?: boolean, showCheck?: boolean, showQuickActions?: boolean, forceQuickActions?: boolean, hasWatchNow: boolean, showPriceDrop?: boolean, showTitle?: boolean, showTvBadge: boolean, isBlocking?: boolean, smallPosters?: boolean, preventDefault?: boolean, resetScrollOnChange?: boolean, hasMoreLikeThis?: boolean, additionalContexts: SnowplowContext[], imgsLoadCount: number, emitOnScroll?: boolean, showPosition?: boolean, showPositionColor: 'primary' | 'brand', fadedEdge?: boolean, isPressPage?: boolean, showFreeTag: boolean, moduleGroup: string, loading: 'lazy' | 'eager' };

const TitleCardPricechange = () =>
	import(/* webpackChunkName: "title-card-pricechange" */ '@/components/list/TitleCardPricechange.vue');





const userStore = storeToRefs(useUserStore());

const isRibbonVisible = computed(() => {
	if (props.showRibbon) {
		return props.showRibbon;
	}

	// make sure we can disable the ribbon specifically
	if (!props.showRibbon && !props.showQuickActions) {
		return false;
	}

	return !userStore.isLoggedIn.value;
});

const sponsoredRecommendationPackageId = computed(() => props.sponsoredAd?.campaign?.watchNowOffer.package.packageId);

const sponsoredRecommendationTitleList = computed(() => props.sponsoredAd?.campaign?.node.nodeId);

function getPositionColor(position: number) {
	if (props.isPressPage) {
		return '#060d18';
	}

	if (props.showPositionColor === 'primary') {
		return '#222c38';
	}

	let numberColor = '#222c38';

	switch (position) {
		case 1:
			numberColor = '#ffd700';
			break;
		case 2:
			numberColor = '#c0c0c0';
			break;
		case 3:
			numberColor = '#cd7f32';
			break;
	}

	return numberColor;
}

function handleClickOut({ title, position }: { title: any; position: number }) {
	emit('onPosterClickOut', {
		index: position,
		title,
		titleList: sponsoredRecommendationTitleList.value,
		event: SraEventAction.Clickout,
	});
}

function handlePosterClick(index: number, title: HorizontalTitleListItem, titleList?: string) {
	emit('onPosterClick', { index, title, titleList });

	if (!!sponsoredRecommendationPackageId.value && props.sponsoredAd?.campaign?.node.nodeId === title.id) {
		const label =
			(props.sponsoredAd?.campaign?.node as any)?.type === 'TECHNICAL_USER_LIST' && titleList
				? `${titleList}_${sponsoredRecommendationPackageId.value}`
				: `${title.id}_${sponsoredRecommendationPackageId.value}`;

		TrackingHelper.trackEvent(ImpressionTrackingEvents.SPONSORED_RECOMMENDATIONS, {
			action: 'click',
			label,
			property: 'poster',
		});
	}
}

const { trackSraEvent } = useBackendTracking();
function impressionCallback(title: HorizontalTitleListItem) {
	const titleId = title.__typename === 'Season' ? title.show.id : title.id;

	if (props.sponsoredAd?.campaign?.node.nodeId === titleId) {
		return trackSraEvent({ action: SraEventAction.Impression, sponsoredAd: () => props.sponsoredAd });
	}
}

function getPosterGraphqlEvents(title: HorizontalTitleListItem, index: number) {
	return {
		impressionCallback: () => impressionCallback(title),
		moreInfosClicked: () => navigateTitleGraphql(title),
		openModal: () => emit('openModal', { index, title }),
		hovered: () => emit('hovered', { index, title }),
		click: () => handlePosterClick(index, title, sponsoredRecommendationTitleList.value),
	};
}

const isMounted = ref(false);
onMounted(() => {
	isMounted.value = true;
	emit('mounted');
});

function getPosterGraphqlProps(title: HorizontalTitleListItem, index: number): PosterGraphqlProps {
	return {
		title: title,
		watchNowOffer: title.watchNowOffer,
		showRibbon: isRibbonVisible.value,
		showQuickActions: props.showQuickActions && userStore.isLoggedIn.value && isMounted.value,
		forceQuickActions: props.forceQuickActions,
		hasMoreLikeThis: props.hasMoreLikeThis,
		showWatchNowButton: props.hasWatchNow && !props.showPriceDrop,
		showTvBadge: props.showTvBadge,
		showNewEpisodesCount: title.watchNowOffer != null && props.showTvBadge,
		clickSource: props.clickSource,
		lazy: index >= props.imgsLoadCount,
		showAvailabilityBadge: true,
		availabilityBadgeSize: 'short',
		noRadius: props.showPriceDrop ? 'bottom' : undefined,
		additionalContexts: props.additionalContexts,
		showFreeTag: props.showFreeTag,
		index,
		moduleGroup: props.moduleGroup,
		loading: props.loading,
	};
}

const isReady = ref(true);
function complete() {
	isReady.value = true;
}

function scrolledRight() {
	if (isReady.value && props.titles.length > 0 && props.titles.length < props.maxTitles) {
		isReady.value = false;
		emit('onScrollRight', complete);
	}
}

const router = useRouter();
function navigateTitleGraphql(title: HorizontalTitleListItem) {
	router.push({ path: title.content.fullPath });
}

/** handle click event coming from Poster */
function clicked(evt: Event, index: number, title: HorizontalTitleListItem) {
	const { className = '', nodeName = '' } = evt.target as HTMLElement;
	const ignoreClassName: string[] = [];
	const ignoreNode = ['SVG', 'PATH'];

	// ignore click from font icons
	if (ignoreNode.indexOf(nodeName.toUpperCase()) !== -1) {
		evt.preventDefault();
		evt.stopPropagation();
		return;
	}

	// ignore click when from ignoreClassName elements
	if (ignoreClassName.indexOf(className) !== -1) {
		evt.preventDefault();
		evt.stopPropagation();
		return;
	}

	// emit click from img
	if (nodeName.toUpperCase() === 'IMG') {
		if (props.preventDefault) {
			// prevent img default behavior
			evt.preventDefault();
			evt.stopPropagation();
		}
		emit('clicked', { index, title });
	}
}

function getTitleObjectId(title: HorizontalTitleListItem) {
	return title.__typename === 'Season' ? title.show?.objectId : title.objectId;
}

function getTitleObjectType(title: HorizontalTitleListItem) {
	return title.__typename === 'Season' ? title?.show?.objectType : title.objectType;
}

function getSeasonNumber(title: HorizontalTitleListItem) {
	return title.__typename === 'Season' ? title.content.seasonNumber : undefined;
}

return { __sfc: true,TitleCardPricechange, props, emit, userStore, isRibbonVisible, sponsoredRecommendationPackageId, sponsoredRecommendationTitleList, getPositionColor, handleClickOut, handlePosterClick, trackSraEvent, impressionCallback, getPosterGraphqlEvents, isMounted, getPosterGraphqlProps, isReady, complete, scrolledRight, router, navigateTitleGraphql, clicked, getTitleObjectId, getTitleObjectType, getSeasonNumber, PosterGraphql, TitleDetailLink, HiddenHorizontalScrollbar }
}

})